export type DateIntervalType = 'day' | 'week' | 'month' | 'year';

export interface DateInterval {
  type: DateIntervalType;
  value: number;
}

const MS_PER_DAY = 86400000;

export function detectInterval(start: Date, end: Date): DateInterval {
  const diffDays = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);

  if (diffDays === 7) {
    return { type: 'week', value: 1 };
  }

  if (isFullCalendarYear(start, end)) {
    return { type: 'year', value: end.getFullYear() - start.getFullYear() + 1 };
  }

  const monthValue = detectMonthInterval(start, end);
  if (monthValue !== null) {
    return { type: 'month', value: monthValue };
  }

  return { type: 'day', value: diffDays };
}

function detectMonthInterval(start: Date, end: Date): number | null {
  const startDay = start.getDate();
  const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

  if (startDay === 1 && isLastDayOfMonth(end)) {
    return monthsDiff + 1;
  }

  if (monthsDiff <= 0) {
    return null;
  }

  if (startDay === end.getDate()) {
    return monthsDiff;
  }

  if (isLastDayOfMonth(start) && isLastDayOfMonth(end)) {
    return monthsDiff;
  }

  return null;
}

function isFullCalendarYear(start: Date, end: Date): boolean {
  return start.getDate() === 1 && start.getMonth() === 0 && end.getMonth() === 11 && isLastDayOfMonth(end);
}

function isLastDayOfMonth(date: Date): boolean {
  return date.getDate() === new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

export function addInterval(date: Date, interval: DateInterval): Date {
  const result = new Date(date);

  switch (interval.type) {
    case 'day':
      result.setDate(result.getDate() + interval.value);
      break;

    case 'week':
      result.setDate(result.getDate() + interval.value * 7);
      break;

    case 'month': {
      const wasLastDay = isLastDayOfMonth(result);
      const originalDay = result.getDate();
      result.setDate(1);
      result.setMonth(result.getMonth() + interval.value);
      const lastDayOfNewMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
      result.setDate(wasLastDay ? lastDayOfNewMonth : Math.min(originalDay, lastDayOfNewMonth));
      break;
    }

    case 'year': {
      const wasLastDay = isLastDayOfMonth(result);
      const originalMonth = result.getMonth();
      const originalDay = result.getDate();
      result.setDate(1);
      result.setFullYear(result.getFullYear() + interval.value);
      const lastDayOfNewMonth = new Date(result.getFullYear(), originalMonth + 1, 0).getDate();
      result.setMonth(originalMonth);
      result.setDate(wasLastDay ? lastDayOfNewMonth : Math.min(originalDay, lastDayOfNewMonth));
      break;
    }
  }

  return result;
}

export function subtractInterval(date: Date, interval: DateInterval): Date {
  return addInterval(date, { ...interval, value: -interval.value });
}
